<template>
  <div v-if="matrix" class="flex flex-col h-full">
    <BaseDetailsPreviewEditHeader
      :is-horizontal-layout="isHorizontalLayout"
      :title="modifiedMatrix?.name"
      link-name="price-policies"
      :mode="mode"
      :is-modified="isModified"
      :is-edit-active="matrix.isFromManager || false"
      @edit-mode="mode = 'edit'"
      @horizontal-layout="(value: boolean) => (isHorizontalLayout = value)"
      @save="onSaveChanges"
      @cancel="onCancelEdit" />

    <AppResizableContainer :layout="isHorizontalLayout ? 'vertical' : 'horizontal'" :minSize="150">
      <template #headerContent>
        <!-- Horizontal Layout -->
        <template v-if="isHorizontalLayout">
          <PricingMatrixCreateEdit
            v-model="modifiedMatrix"
            :matrix="matrix"
            :mode="mode"
            :isHorizontalLayout="isHorizontalLayout"
            :key="matrix?.id" />
        </template>

        <!-- Vertical Layout -->
        <template v-else>
          <PerfectScrollbar class="w-full h-full py-4 space-y-2" ref="scrollbarId">
            <PricingMatrixCreateEdit
              v-model="modifiedMatrix"
              :matrix="matrix"
              :mode="mode"
              :isHorizontalLayout="isHorizontalLayout"
              :key="matrix?.id" />
          </PerfectScrollbar>
        </template>
      </template>

      <template #detailsTitle>
        <nav
          role="section-nav"
          class="flex justify-between px-6 text-sm font-medium text-center text-gray-500 sm:px-0 min-h-9">
          <ul class="flex flex-wrap items-center gap-4 pl-4 -mb-px">
            <li
              v-for="tab in detailsTabs"
              :key="tab.name"
              @click="selectedDetailsTab = tab.name"
              class="py-1.5 cursor-pointer"
              :class="selectedDetailsTab === tab.name ? 'border-b-2 border-theme-500' : ''">
              <p>
                {{ tab.label }}
              </p>
            </li>
          </ul>
        </nav>
      </template>

      <template #detailsContent>
        <BaseTable
          v-if="modifiedMatrixDetails && selectedDetailsTab == 'materials'"
          :headers="tableHeaders"
          :list="modifiedMatrixDetails"
          :total-records="matrixDetailsTotalCount"
          :is-add-new-hidden="mode == 'view'"
          :is-search-hidden="mode == 'edit'"
          :hide-multiselect-delete="mode == 'view'"
          isSavingLayoutHidden
          :hide-grouping="true"
          clickable
          @table-item-clicked="onEditMatrixDetail"
          table-id="matrixDetails"
          @add-new="onAddNewMatrixDetail"
          @deleteMultiselect="onDeleteMultiselect"
          @refresh-data="refreshData">
          <template #actions="{ item }: any">
            <div
              class="flex items-center justify-center gap-1"
              v-if="item.isFromManager && mode === 'edit'">
              <button
                type="button"
                class="rounded-full p-1.5 text-red-400 hover:bg-red-50"
                @click="onDeleteRow(item as PricingPolicyMatrixDetail)">
                <IconTrashOutline class="size-4" />
              </button>
            </div>
          </template>
        </BaseTable>

        <PricingMatrixDetailsCustomersTable
          v-if="modifiedMatrixDetails && selectedDetailsTab == 'customers'"
          :mode="mode"
          :selected-page="customersForPolicySelectedPage"
          :table-data="customersForPolicyTableData"
          :table-data-count="customersForPolicyTableDataTotalCount"
          :added-customers="modifiedMatrix?.modifiedCustomers || []"
          @add-customers="onAddCustomers"
          @delete-customers="onDeleteCustomers"
          @refresh-table-data="onRefreshCustomersTableData"
          @add-all-customers="onAddAllCustomers" />
      </template>
    </AppResizableContainer>
    <PricingMatrixDetailsModalCreateEdit
      v-if="createEditModalActive"
      :item="matrixDetail"
      :mode="createEditModalMode"
      @save="onSaveMatrixDetail"
      @close="createEditModalActive = false">
    </PricingMatrixDetailsModalCreateEdit>
    <BaseDrawerConfirm
      :is-active="confirmModalActive"
      @cancel="confirmModalActive = false"
      @confirm="onDiscardChanges" />
    <BaseModalConfirm
      v-if="deleteRowItem && modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle">
      <template #title>{{ $t('common.actions.delete') }}</template>
      <template #text>
        {{ $t('messages.info.deleteSingle', { 'item': deleteRowItem?.material?.name }) }}
      </template>
      <template #buttonText>{{ $t('common.actions.delete') }}</template>
    </BaseModalConfirm>
  </div>
</template>

<script setup lang="ts">
import AppResizableContainer from '@/components/common/AppResizableContainer.vue';
import BaseDetailsPreviewEditHeader from '@/components/common/BaseDetailsPreviewEditHeader.vue';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseDrawerConfirm from '@/components/common/BaseDrawerConfirm.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import PricingMatrixCreateEdit from '@/components/prices/PricingMatrixCreateEdit.vue';
import PricingMatrixDetailsModalCreateEdit from '@/components/prices/PricingMatrixDetailsModalCreateEdit.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import { v4 as uuidv4 } from 'uuid';
import useVuelidate from '@vuelidate/core';
import PricingMatrixDetailsCustomersTable from '@/components/prices/PricingMatrixDetailsCustomersTable.vue';

import type {
  PricingPolicyMatrix,
  PricingPolicyMatrixDetail,
  PricingPolicy,
} from '@/types/PricingTypes';
import { RecordMode, type TableFilterData } from '@/types/Base';

import { filterData, sortData } from '@/utils/table';
import {
  getPricingPolicyMatrixById,
  getPricingPolicyMatrixDetailsWithConditions,
  addOrEditPricingPolicyMatrix,
  addOrEditPricingPolicyMatrixDetails,
  removePricingPolicyMatrixDetailsByIds,
  addAllCustomersToPricePolicy,
} from '@/services/api/prices';

import { onMounted, ref, watchEffect, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { deepCopy } from '@/utils/data';
import type { Customer } from '@/types/Traders';
import { getCustomersWithConditions } from '@/services/api/traders';
import { useI18n } from 'vue-i18n';

const route = useRoute();
const router = useRouter();

const $v = useVuelidate();

const matrix = ref<PricingPolicyMatrix>();
const modifiedMatrix = ref<PricingPolicyMatrix>();
const isHorizontalLayout = ref(true);
const mode = ref<'view' | 'edit'>('view');
const matrixDetails = ref<PricingPolicyMatrixDetail[]>([]);
const modifiedMatrixDetails = ref<PricingPolicyMatrixDetail[]>([]);
const matrixDetailsTotalCount = ref<number>();
const matrixDetail = ref<PricingPolicyMatrixDetail>(initializeMatrixDetail());

watch(
  () => mode.value,
  (newMode) => {
    const actionsColumn = tableHeaders.value.find((header) => header.value === 'actions');
    if (actionsColumn) {
      actionsColumn.active = newMode === 'edit';
    }
  }
);

const { t: $t } = useI18n({ useScope: 'global' });
const tableHeaders = ref([
  { value: 'discountSequence', text: $t('baseObject.sequence'), type: 'number', active: true },
  {
    value: 'materialGroup.name',
    text: $t('discountMatrix.materialGroup'),
    type: 'text',
    active: true,
  },
  {
    value: 'traderSiteGroup.name',
    text: $t('discountMatrix.traderSiteGroup'),
    type: 'text',
    active: true,
  },
  { value: 'material.name', text: $t('material.material'), type: 'text', active: true },
  { value: 'traderSite.name', text: $t('discountMatrix.traderSite'), type: 'text', active: true },
  { value: 'value', text: $t('discountMatrix.discount'), type: 'text', active: true },
  {
    value: 'discountTypeToken',
    text: $t('discountMatrix.discountType'),
    type: 'text',
    active: true,
    isSortHidden: true,
  },
  { value: 'actions', text: $t('baseObject.actions'), active: false },
]);

type QueryParams = {
  page?: number;
  itemsPerPage: number;
  query?: string;
  sortColumn?: string;
  sortAscending?: boolean;
  isActive?: boolean;
};

onMounted(async () => {
  await loadData();
  await loadCustomersForPricePolicy({ page: 1, itemsPerPage: 10 });
});

async function loadData() {
  const responseMatrix = await getPricingPolicyMatrixById(route.params.id as string);
  if (responseMatrix) {
    matrix.value = responseMatrix;
    mode.value = 'view';
    modifiedMatrix.value = deepCopy<PricingPolicyMatrix>(matrix.value);

    await loadMatrixDetails({
      itemsPerPage: 50,
      sortColumn: 'discountSequence',
      sortAscending: true,
      page: 1,
    });
  } else {
    matrix.value = newMatrix();
    modifiedMatrix.value = deepCopy<PricingPolicyMatrix>(matrix.value);
    mode.value = 'edit';
  }
}

async function loadMatrixDetails(params: QueryParams, reset?: boolean) {
  const responseDiscountMatrixDetails = await getPricingPolicyMatrixDetailsWithConditions({
    ...params,
    matrixId: modifiedMatrix.value?.id || '',
  });
  if (responseDiscountMatrixDetails) {
    responseDiscountMatrixDetails.data.forEach((detail) => {
      if (detail.discountType === 0) detail.discountTypeToken = '%';
      else detail.discountTypeToken = '€';
    });
    matrixDetails.value = responseDiscountMatrixDetails.data;
    if (reset)
      modifiedMatrixDetails.value = deepCopy<PricingPolicyMatrixDetail[]>(matrixDetails.value);
    mode.value = 'view';
    modifiedMatrixDetails.value = deepCopy<PricingPolicyMatrixDetail[]>(matrixDetails.value);
    matrixDetailsTotalCount.value = responseDiscountMatrixDetails.count;
  }
}

function newMatrix(): PricingPolicyMatrix {
  const pricingPolicy: PricingPolicy = {
    id: uuidv4(),
    companyId: '',
    pricingType: 1,
    isActive: true,
    isFromManager: true,
  };
  return {
    id: String(route.params.id),
    code: '',
    name: '',
    pricingPolicy: pricingPolicy,
    pricingPolicyId: pricingPolicy.id,
    pricingPolicyCatalogOrderHeaderId: '',
    isActive: true,
    isFromManager: true,
  } as PricingPolicyMatrix;
}

async function refreshData(data: TableFilterData) {
  if (mode.value == 'view') {
    const sortColumn =
      data.column !== undefined ? tableHeaders.value[data.column]?.value : 'discountSequence';
    const sortAscending = data.order === 'asc' || data.order === undefined;
    const params: QueryParams = {
      page: data.page || 1,
      itemsPerPage: data.itemsPerPage || 50,
      query: data.query || '',
      sortColumn: sortColumn,
      sortAscending: sortAscending,
      isActive: data.isActive,
    };

    await loadMatrixDetails(params);

    if (data.query && matrixDetails.value)
      matrixDetails.value = filterData(matrixDetails.value, data.query, [
        'discountSequence',
        'materialGroup.name',
        'traderSiteGroup.name',
        'material.name',
        'traderSite.name',
      ]);
    if (data.column !== undefined && data.order && matrixDetails.value)
      matrixDetails.value = sortData(
        matrixDetails.value,
        data.column,
        data.order,
        tableHeaders.value
      );
  } else {
    if (data.column !== undefined && data.order && modifiedMatrixDetails.value) {
      modifiedMatrixDetails.value = sortData(
        modifiedMatrixDetails.value,
        data.column,
        data.order,
        tableHeaders.value
      );
      matrixDetails.value = sortData(
        matrixDetails.value,
        data.column,
        data.order,
        tableHeaders.value
      );
    }
  }
}

const createEditModalActive = ref(false);
const createEditModalMode = ref<'view' | 'edit'>('view');

function initializeMatrixDetail(): PricingPolicyMatrixDetail {
  return {
    id: uuidv4(),
    pricingPolicyDetailMatrixHeaderId: String(route.params.id),
    traderSiteGroupId: '',
    traderSiteId: '',
    materialGroupId: '',
    materialId: '',
    discountType: 0, // 0: Percent, 1: Value
    value: 0,
    discountSequence: 1,
    isActive: true,
    isFromManager: true,
    modified: true,
  };
}

function onAddNewMatrixDetail() {
  createEditModalActive.value = true;
  createEditModalMode.value = 'edit';
  matrixDetail.value = initializeMatrixDetail();
}

function onEditMatrixDetail(item: PricingPolicyMatrixDetail) {
  matrixDetail.value = item;
  createEditModalActive.value = true;
  createEditModalMode.value = mode.value;
}

const modalDeleteRow = ref(false);

const deleteRowItem = ref<PricingPolicyMatrixDetail>();
function onDeleteRow(item: PricingPolicyMatrixDetail) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

const idsToDelete = ref<string[]>([]);

function onDeleteMultiselect(items: PricingPolicyMatrixDetail[]) {
  const deleteIds = items.filter((item) => item.isFromManager === true).map((item) => item.id);
  idsToDelete.value.concat(deleteIds);
  modifiedMatrixDetails.value = modifiedMatrixDetails.value.filter(
    (detail) => !detail.id || !deleteIds.includes(detail.id)
  );

  idsToDelete.value.push(
    ...items.filter((item) => item.createdDate && item.isFromManager).map((item) => item.id)
  );
}

function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!deleteRowItem.value) return;
  const idToDelete = deleteRowItem.value.id;
  modifiedMatrixDetails.value = modifiedMatrixDetails.value.filter(
    (detail) => detail.id !== idToDelete
  );
  if (deleteRowItem.value.createdDate) {
    idsToDelete.value.push(deleteRowItem.value.id);
  }
}

function onSaveMatrixDetail(item: PricingPolicyMatrixDetail) {
  const index = modifiedMatrixDetails.value.findIndex((detail) => detail.id === item.id);
  if (index !== -1) modifiedMatrixDetails.value[index] = item;
  else modifiedMatrixDetails.value.push(item);
  createEditModalActive.value = false;
}

const isModified = ref<boolean>(false);
watchEffect(() => {
  isModified.value =
    JSON.stringify(modifiedMatrix.value) !== JSON.stringify(matrix.value) ||
    JSON.stringify(modifiedMatrixDetails.value) !== JSON.stringify(matrixDetails.value);
});

const confirmModalActive = ref(false);
const isBackMode = ref(false);

function onCancelEdit(backMode: string) {
  $v.value.$reset();
  if (isModified.value) {
    confirmModalActive.value = true;
    if (backMode == 'back') {
      isBackMode.value = true;
    }
  } else mode.value = 'view';
}

async function onDiscardChanges() {
  mode.value = 'view';
  confirmModalActive.value = false;
  if (matrix.value) modifiedMatrix.value = deepCopy<PricingPolicyMatrix>(matrix.value);
  modifiedMatrixDetails.value = deepCopy<PricingPolicyMatrixDetail[]>(matrixDetails.value);
  idsToDelete.value = [];
  if (!modifiedMatrix.value?.createdDate || isBackMode.value) {
    router.push('/price-policies');
  }
}

async function onSaveChanges() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  mode.value = 'view';
  if (!isModified.value) return;
  if (JSON.stringify(modifiedMatrix.value) !== JSON.stringify(matrix.value)) {
    const {
      id,
      code,
      name,
      pricingPolicyId,
      pricingPolicyCatalogOrderHeaderId,
      isActive,
      isFromManager,
      pricingPolicy,
    } = modifiedMatrix?.value || {};

    const data = {
      id,
      code,
      name,
      pricingPolicyId,
      pricingPolicyCatalogOrderHeaderId,
      isActive,
      isFromManager,
      pricingPolicy,
      modifiedCustomers: modifiedMatrix.value?.modifiedCustomers,
    } as PricingPolicyMatrix;
    await addOrEditPricingPolicyMatrix(data);
  }
  if (modifiedMatrixDetails.value) {
    await addOrEditPricingPolicyMatrixDetails(
      modifiedMatrixDetails.value.filter((x) => x.modified)
    );
  }
  if (idsToDelete.value.length) {
    await removePricingPolicyMatrixDetailsByIds(idsToDelete.value);
  }

  await loadData();
  await loadMatrixDetails(
    {
      itemsPerPage: 50,
      sortColumn: 'discountSequence',
      sortAscending: true,
      page: 1,
    },
    true
  );
  idsToDelete.value = [];
  await loadCustomersForPricePolicy({
    page: 1,
    itemsPerPage: customersForPolicyItemsPerPage.value,
  });
}

const detailsTabs = ref([
  {
    name: 'materials',
    label: 'Materials',
  },
  {
    name: 'customers',
    label: 'Customers',
  },
]);

const selectedDetailsTab = ref('materials');

const customersForPolicyTableData = ref<Customer[]>([]);
const customersForPolicyTableDataTotalCount = ref<number>(0);
const customersForPolicySelectedPage = ref(1);
const customersForPolicyItemsPerPage = ref(10);

async function loadCustomersForPricePolicy(data: TableFilterData) {
  if (!modifiedMatrix.value) return;
  const currentPage = data.page ?? 1;
  customersForPolicyItemsPerPage.value = data.itemsPerPage ?? 10;

  let addedCustomers: Customer[] = [];
  if (modifiedMatrix.value.modifiedCustomers) {
    addedCustomers = modifiedMatrix.value.modifiedCustomers.filter(
      (c) => c.recordMode == RecordMode.Added
    );
  }

  let serverCustomers: Customer[] = [];
  let serverCustomersCount: number = 0;
  const res = await getCustomersWithConditions({
    ...data,
    pricingPolicyId: modifiedMatrix.value.pricingPolicyId,
  });
  if (res) {
    serverCustomers = res.data;
    serverCustomersCount = res.count;
  }
  const serverPages = Math.ceil(serverCustomersCount / customersForPolicyItemsPerPage.value);
  const serverPageItemCount = serverCustomers.length;
  const pageAvailableSpace = Math.max(
    0,
    customersForPolicyItemsPerPage.value - serverPageItemCount
  );

  if (currentPage < serverPages) {
    // get page from server
    customersForPolicyTableData.value = serverCustomers;
  } else if (currentPage > serverPages) {
    // Get page from local data
    const mod = serverCustomersCount % customersForPolicyItemsPerPage.value;
    const availableSpace = mod === 0 ? 0 : customersForPolicyItemsPerPage.value - mod;
    const modifiedCurrentPage = currentPage - serverPages;
    const startIndex =
      (modifiedCurrentPage - 1) * customersForPolicyItemsPerPage.value + availableSpace;
    const endIndex = startIndex + customersForPolicyItemsPerPage.value;
    customersForPolicyTableData.value = addedCustomers.slice(startIndex, endIndex);
  } else {
    // Get boundary page (last server + fill with modified)
    if (pageAvailableSpace > 0) {
      const modifiedData = addedCustomers.slice(0, pageAvailableSpace);
      customersForPolicyTableData.value = [...serverCustomers, ...modifiedData];
    } else {
      customersForPolicyTableData.value = serverCustomers;
    }
  }

  customersForPolicyTableDataTotalCount.value = serverCustomersCount + addedCustomers.length;

  let deletedCustomers: Customer[] = [];
  if (modifiedMatrix.value.modifiedCustomers) {
    deletedCustomers = modifiedMatrix.value.modifiedCustomers.filter(
      (c) => c.recordMode == RecordMode.Deleted
    );
  }

  if (deletedCustomers.length) {
    deletedCustomers.forEach((deleted) => {
      const existing = customersForPolicyTableData.value.find((x) => x.id === deleted.id);
      if (existing) existing.deleted = true;
    });
  }
}

async function onAddCustomers(customers: Customer[]) {
  if (!modifiedMatrix.value) return;
  if (!modifiedMatrix.value.modifiedCustomers) modifiedMatrix.value.modifiedCustomers = [];
  modifiedMatrix.value.modifiedCustomers.push(...customers);

  // This will trigger load table data from refreshing table to last page
  const lastPage = Math.ceil(
    customersForPolicyTableDataTotalCount.value / customersForPolicyItemsPerPage.value
  );
  if (customersForPolicySelectedPage.value === lastPage)
    await loadCustomersForPricePolicy({
      page: lastPage,
      itemsPerPage: customersForPolicyItemsPerPage.value,
    });
  else customersForPolicySelectedPage.value = lastPage;
}

function onDeleteCustomers(customers: Customer[]) {
  const matrix = modifiedMatrix.value;
  if (!matrix) return;
  customers.forEach((customer) => {
    if (!matrix.modifiedCustomers) matrix.modifiedCustomers = [];
    const existingIndex = matrix.modifiedCustomers.findIndex((x) => x.id === customer.id);
    if (existingIndex !== -1) {
      const existingCustomer = matrix.modifiedCustomers[existingIndex];

      if (existingCustomer.recordMode === RecordMode.Added) {
        matrix.modifiedCustomers.splice(existingIndex, 1);
      }

      const existingIndexInTable = customersForPolicyTableData.value.findIndex(
        (c) => c.id === customer.id
      );
      if (existingIndexInTable !== -1)
        customersForPolicyTableData.value.splice(existingIndexInTable, 1);
    } else {
      const existing = customersForPolicyTableData.value.find((x) => x.id == customer.id);
      if (existing) existing.deleted = true;
      customer.recordMode = RecordMode.Deleted;
      matrix.modifiedCustomers.push(customer);
    }
  });
}

function onRefreshCustomersTableData(data: TableFilterData) {
  loadCustomersForPricePolicy(data);
}

async function onAddAllCustomers() {
  if (!modifiedMatrix.value) return;
  modifiedMatrix.value.modifiedCustomers = [];
  await addAllCustomersToPricePolicy(modifiedMatrix.value.pricingPolicyId);
  await onSaveChanges();
}
</script>
